// Import the global type declaration
import "@rapid/types/window.d.ts";
import { SegmentAnalyticsAdapter } from "./adapters/segment";
import { RfAnalyticsAdapter } from "./adapters/rf";
import { PostHogAnalyticsAdapter } from "./adapters/posthog";
import {
    AnalyticsMethod,
    AnalyticsIdentity,
    AppSource,
    AnalyticsEventData,
    AppSourceString
} from "./types";

import { v4 as uuidv4 } from "uuid";
import { AnalyticsAbstractAdapter } from "./adapters/abstract";

export type EventTrackerTrackResult = Promise<
    | { results: PromiseSettledResult<unknown>[]; errors: never[] }
    | { results: never[]; errors: unknown[] }
>;

export type EventTrackerTrack = ({
    method,
    id,
    data,
    identity
}: {
    method: AnalyticsMethod;
    id?: string;
    data?: Record<string, any>;
    identity?: AnalyticsIdentity;
}) => EventTrackerTrackResult;

class EventTracker {
    private adapters: AnalyticsAbstractAdapter[] = [
        SegmentAnalyticsAdapter.getInstance(),
        RfAnalyticsAdapter.getInstance(),
        PostHogAnalyticsAdapter.getInstance()
    ];

    private appSource: AppSourceString = AppSource.RAPID;

    setAppSource(source: AppSourceString) {
        this.appSource = source;
    }

    trackEvent: EventTrackerTrack = async ({ method, id, data, identity }) => {
        try {
            let eventData: AnalyticsEventData = {
                ...(data || {}),
                _anonymousId: window?.analytics?.user?.().anonymousId?.() || uuidv4(),
                appSource: this.appSource
            };

            if (identity) {
                const {
                    id,
                    email,
                    name,
                    displayName,
                    workspaceId,
                    workspaceUrl,
                    workspaceName,
                    workspacePlan,
                    workflowId,
                    workflowUrl,
                    workflowName
                } = identity;
                eventData = {
                    ...eventData,
                    id,
                    email,
                    name,
                    displayName,
                    workspaceId,
                    workspaceUrl,
                    workspaceName,
                    workspacePlan,
                    workflowId,
                    workflowUrl,
                    workflowName
                };
            }

            const eventPayload = { method, id, data: eventData };

            if (!eventData.id) eventData.id = eventData._anonymousId;

            const results = await Promise.allSettled(
                this.adapters.map((adapter) => adapter.onEvent(eventPayload))
            );

            results
                .filter((r) => r.status === "rejected")
                .forEach((r) => {
                    console.error(`[Analytics] ${method} error:`, r.reason, id, data);
                });

            return { results, errors: [] };
        } catch (err) {
            console.error(`[Analytics] ${method} error:`, err, id, data);
            return { results: [], errors: [err] };
        }
    };
}

// singleton instance
const eventTracker = new EventTracker();

export default eventTracker;
